import { is, List } from "immutable";
import _ from "lodash";
import * as moment from "moment";
import * as QueryString from "querystringify";
import * as React from "react";
import * as BS from "react-bootstrap";
import * as BT from "react-bootstrap-table";
import { connect } from "react-redux";
import { Link, RouteComponentProps, withRouter } from "react-router-dom";
import * as redux from "redux";
import * as Actions from "../../../actions";
import * as Comp from "../../../components";
import { Paths } from "../../../constants";
import * as M from "../../../models";
import { Api, ApiEndpoint } from "../../../services/Api";
import Settings from "../../../settings";
import { AppState } from "../../../store";
import { DocumentHelper, ReactHelper, Utility } from "../../../utils";
import { AbstractsViewSwitcher } from "../components";

const mapStateToProps = (state: AppState, ownProps: OwnProps | undefined): ConnectedState => ({
    ...Comp.Base.DefaultListViewStatePropsMap(state, ownProps),
    abstracts: state.adminAbstracts.listAbstracts,
    FDs: state.adminAbstracts.FDs,
    loadingAbstracts: state.adminAbstracts.loadingListAbstracts,
    loadingFDs: state.adminAbstracts.loadingFDs,
});

const mapDispatchToProps = (dispatch: redux.Dispatch<AppState>): ConnectedDispatch => ({
    ...Comp.Base.DefaultDispatchPropsMap(dispatch),
    loadAbstracts: (eventId: number) => dispatch(Actions.AdminAbstract.loadListAbstractsForEvent(eventId)),
    loadFDs: (eventId: number) => dispatch(Actions.AdminAbstract.loadFinancialDisclosures(eventId)),
    toggleAbstractsLoading: (loading: boolean) => dispatch(Actions.AdminAbstract.toggleLoadingListAbstracts(loading)),
    toggleFDsLoading: (loading: boolean) => dispatch(Actions.AdminAbstract.toggleLoadingFDs(loading)),
    updateAbstract: (abstract: M.Abstract.AbstractListItem) => dispatch(Actions.AdminAbstract.updateListAbstract(abstract)),
    updateAbstracts: (abstracts: List<M.Abstract.AbstractListItem>) => dispatch(Actions.AdminAbstract.updateListAbstracts(abstracts)),
});

type OwnProps = RouteComponentProps<{}> & {
    eventId: number;
};

type ConnectedState = Comp.Base.DefaultListViewConnectedState & {
    abstracts: List<M.Abstract.AbstractListItem>;
    FDs: List<M.FinancialDisclosure.FD>;
    loadingAbstracts: boolean;
    loadingFDs: boolean;
};

type ConnectedDispatch = Comp.Base.DefaultConnectedDispatch & {
    loadAbstracts: (eventId: number) => void;
    loadFDs: (eventId: number) => void;
    toggleAbstractsLoading: (loading: boolean) => void;
    toggleFDsLoading: (loading: boolean) => void;
    updateAbstract: (abstract: M.Abstract.AbstractListItem) => void;
    updateAbstracts: (abstracts: List<M.Abstract.AbstractListItem>) => void;
};

interface OwnState {
    authors: List<M.Abstract.AbstractAuthorTableItem>;
    appliedFilters: M.Search.AppliedFilter[];
    doFilter: boolean;
    filteredData: List<M.Abstract.AbstractAuthorTableItem>;
    generatingExport: boolean;
    selectedIds: string[];
    sendingFdRequest: M.Generic.ItemLoadingState;
    sendingFdRequestBatch: boolean;
    statuses: M.Search.SearchFilter[];
}

interface UrlQuery {
    status?: string;
}

type FilterType = "status";

type CombinedProps = ConnectedState & ConnectedDispatch & OwnProps;

class DisclosuresComponent extends Comp.Base.BaseListViewComponent<CombinedProps, OwnState> {

    public constructor(props: CombinedProps) {
        super(props);

        const statuses = M.Generic.typeDefinitionsAsSearchFilter(M.FinancialDisclosure.DisclosureStatuses.Values);

        this.state = {
            ...this.getInitialListViewState(props),
            authors: List(),
            appliedFilters: [],
            doFilter: false,
            filteredData: List(),
            generatingExport: false,
            selectedIds: [],
            sendingFdRequest: {},
            sendingFdRequestBatch: false,
            statuses,
        };
    }

    public async componentDidMount() {
        this._isMounted = true;
        await this.initializeComponent();
    }

    public componentDidUpdate(prevProps: Readonly<CombinedProps>) {
        if (!is(prevProps.abstracts, this.props.abstracts))
            this.loadAbstractsFromState();

        if (!is(prevProps.FDs, this.props.FDs))
            this.loadFDsFromState();

        if (!is(prevProps.tableState, this.props.tableState))
            this.clearSelectedAbstractIds();

        // just in case a different guid in URL has been detected
        if (prevProps.eventId !== this.props.eventId) {
            this.clearState(() => {
                this.initializeComponent();
            });
        }
    }

    public componentWillUnmount() {
        this._isMounted = false;
    }

    public shouldComponentUpdate?(nextProps: Readonly<OwnProps>, nextState: Readonly<OwnState>, nextContext: any): boolean {
        if (nextState.doFilter) {
            this.filterData();
            return false;
        }

        if (nextProps.location.search !== this.props.location.search) {
            this.updateAllFilterChecks(nextProps.location.search);
            return false;
        }

        return true;
    }

    public render() {
        const loading = this.state.loading || this.props.loadingAbstracts || this.props.loadingFDs;

        const actionsCommon = <AbstractsViewSwitcher eventId={this.props.eventId} view="Disclosures" />;

        const actionsPrimary = <BS.ButtonToolbar>
            <BS.ButtonGroup>
                <BS.Button
                    disabled={this.state.generatingExport}
                    onClick={() => this.generateExport()}
                >
                    {this.state.generatingExport ? <><Comp.Generic.LoadingIcon /> Generating export&hellip;</> : "Export"}
                </BS.Button>
                <BS.Button
                    disabled={this.state.selectedIds.length === 0 || this.state.sendingFdRequestBatch}
                    id="assign-group-btn"
                    onClick={() => this.sendFdRequest(this.state.selectedIds, true)}
                >
                    {this.state.sendingFdRequestBatch
                        ? <><Comp.Generic.LoadingIcon /> Sending request&hellip;</>
                        : "Request disclosure"}
                </BS.Button>
            </BS.ButtonGroup>
        </BS.ButtonToolbar>;

        const tableActions = this.getTableActions({
            dataLength: this.state.authors.size,
            filteredLength: this.state.filteredData.size,
            searchPlaceholder: "Search disclosures...",
        });

        const checkedStatus = this.state.statuses.filter((x) => x.checked).length
            ? this.state.statuses.filter((x) => x.checked)[0].name
            : "All";

        const abstractListLink = this.lastPathIsFromListView()
            ? <a href="#" onClick={(e) => { ReactHelper.emptyClick(e); window.history.back(); }}>Abstracts</a>
            : <Link to={Paths.AdminAbstractList(this.props.eventId)}>Abstracts</Link>;

        return <Comp.Page.AdminPage
            actionsCommon={actionsCommon}
            actionsPrimary={actionsPrimary}
            actionsSecondary={tableActions}
            preContent={<h1>{abstractListLink} &raquo; Financial Disclosures</h1>}
            mainClass="results"
            title={"Abstracts"}
        >

            <Comp.Generic.LoadingSpinner show={loading} />

            {this.getErrorAlert()}

            <div className="results-filters">
                {loading ? <Comp.Generic.WrapperLess><Comp.Generic.LoadingIcon /> Loading&hellip;</Comp.Generic.WrapperLess> : null}

                <h3>Status</h3>

                <BS.DropdownButton id="filters-status" title={checkedStatus}>
                    <BS.MenuItem key={0} onClick={() => this.clearFilters()}>
                        All
                    </BS.MenuItem>
                    {_.orderBy(this.state.statuses, (x) => x.id).map((s, i) => (
                        <BS.MenuItem key={i} onClick={() => this.onFilterCheck(s.id, true, "status")}>
                            {s.name} <span className="mute">({s.count})</span>
                        </BS.MenuItem>
                    ))}
                </BS.DropdownButton>
            </div>

            <div className="results-list">

                <Comp.Search.AppliedFilters
                    filters={this.state.appliedFilters}
                    onClearAll={() => this.clearFilters()}
                    onClearFilter={(id, type) => this.onFilterCheck(id, false, type as FilterType)}
                />

                <BT.BootstrapTable
                    bordered={false}
                    data={this.state.filteredData.toArray()}
                    pagination={this.state.tablePageSize > -1}
                    selectRow={{
                        bgColor: "#f7f7f7",
                        className: "select-abstracts-checkbox",
                        mode: "checkbox",
                        selected: [...this.state.selectedIds],
                        onSelect: (row: M.Abstract.AbstractAuthorTableItem, isSelected: boolean, event: any, rowIndex: number) => {
                            const selectedIds = [...this.state.selectedIds];
                            Utility.addToOrRemoveFromArray(selectedIds, row.lineId, isSelected);
                            this.setState({ ...this.state, selectedIds });
                        },
                        onSelectAll: (isSelected: boolean, rows: M.Abstract.AbstractAuthorTableItem[]) => {
                            this.setState({ ...this.state, selectedIds: isSelected ? rows.map((r) => r.lineId) : [] });
                            return true;
                        },
                    }}
                    options={{
                        afterSearch: (search, results) => this.onTableSearch(search, results, () => this.afterSearch(search, results)),
                        page: this.state.tablePage,
                        paginationPanel: () => <div />,
                        sizePerPage: this.state.tablePageSize,
                        onSortChange: (sortName: any, sortOrder: any) => this.handleTableSort(sortOrder, sortName),
                    }}
                    ref={(ref: any) => this.table = ref}
                >
                    <BT.TableHeaderColumn dataField="lineId" isKey hidden></BT.TableHeaderColumn>
                    <BT.TableHeaderColumn dataField="abstractId" hidden searchable></BT.TableHeaderColumn>
                    <BT.TableHeaderColumn dataField="abstractIdSearch" hidden searchable></BT.TableHeaderColumn>

                    <BT.TableHeaderColumn
                        dataField="abstractTitle"
                        dataFormat={(cell: any, row: M.Abstract.AbstractAuthorTableItem) => {
                            return <Comp.Generic.WrapperLess>
                                <Link to={Paths.AdminAbstractDetails(row.eventId, row.abstractGuid)} dangerouslySetInnerHTML={{ __html: cell }} />
                                <span className="mute">
                                    <br />#{row.abstractId} &nbsp;&nbsp; {row.authorName}
                                </span>
                            </Comp.Generic.WrapperLess>;
                        }}
                        dataSort
                        width="30%"
                    >Abstract title</BT.TableHeaderColumn>

                    <BT.TableHeaderColumn
                        dataField="fullName"
                        dataFormat={(cell: any, row: M.Abstract.AbstractAuthorTableItem) => {
                            return <Comp.Generic.WrapperLess>
                                {row.fullName}
                                <span className="mute">
                                    <br />{`${row.isPrimary ? "A" : "Co-a"}uthor`}
                                    <br /><a href={`mailto:${row.email}`}>{row.email}</a>
                                </span>
                            </Comp.Generic.WrapperLess>;
                        }}
                        dataSort
                    >Author name</BT.TableHeaderColumn>

                    <BT.TableHeaderColumn
                        dataField="FDsDisplay"
                        dataFormat={(cell: any, row: M.Abstract.AbstractAuthorTableItem) => {
                            if (!row.FD) {
                                const sending = !!this.state.sendingFdRequest[row.lineId];
                                return <Comp.Generic.WrapperLess>
                                    {sending
                                        ? <span><Comp.Generic.LoadingIcon /> Sending request&hellip;</span>
                                        : <a href="#" onClick={(e) => { ReactHelper.emptyClick(e); this.sendFdRequest([row.lineId], false); }}>
                                            Request disclosure
                                        </a>}
                                    {row.lastFdRequestSent
                                        ? <span className="mute"><br />Request sent {moment(Utility.utcToLocal(row.lastFdRequestSent)).format("MMM D, YYYY")}</span>
                                        : null}
                                </Comp.Generic.WrapperLess>;
                            }

                            return row.FDsDisplay;
                        }}
                        width="28%"
                    >Disclosures</BT.TableHeaderColumn>

                    <BT.TableHeaderColumn
                        dataField="isCoi"
                        dataFormat={(cell: any, row: M.Abstract.AbstractAuthorTableItem) => {
                            return <label>
                                <input type="checkbox" onChange={() => this.updateCoiFlag(row.abstractId, !row.isCoi)} checked={row.isCoi} />
                            </label>;
                        }}
                        dataSort
                        width="5%"
                    >COI</BT.TableHeaderColumn>
                </BT.BootstrapTable>
            </div>

        </Comp.Page.AdminPage>;
    }

    private async afterSearch(search: string, results: ReadonlyArray<M.Abstract.AbstractAuthorTableItem>) {
        if (search) {
            await this.updateFilterCounts();
        }
        else {
            await this.filterData();
            await this.updateFilterCounts();
        }
    }

    /**
     * Triggers a clear of all currently applied filters
     */
    private clearFilters() {
        this.props.replaceUrlQuery({
            status: "",
        });
        this.saveTableState();
    }

    private async clearSelectedAbstractIds() {
        await this.setStateAsync({ ...this.state, selectedIds: [] });
    }

    private clearState(callback?: () => void) {
        this.setState({
            authors: List(),
            doFilter: false,
            filteredData: List(),
            selectedIds: [],
        }, callback);
    }

    private async filterData() {
        const filteredData: M.Abstract.AbstractAuthorTableItem[] = [];
        const urlQuery = QueryString.parse(this.props.location.search) as UrlQuery;

        // setup id arrays from url query
        const statusIds = (urlQuery.status || "").split(",").filter((x) => x !== "").map((x) => parseInt(x, 10));
        // can only have one status ID
        const statusId = statusIds.length ? statusIds[0] : undefined;

        const list = this.state.tableSearchResults
            ? List(this.state.tableSearchResults)
            : this.state.authors;

        list.forEach((x: M.Abstract.AbstractAuthorTableItem) => {
            let isFiltered = true;

            // special case for status ID if COI
            if (statusId === M.FinancialDisclosure.DisclosureStatuses.IsCOI.id)
                isFiltered = x.isCoi;
            else if (statusId !== undefined)
                isFiltered = x.statusId === statusId;

            if (isFiltered) {
                filteredData.push(x);
            }
        });

        await this.setStateAsync({
            ...this.state,
            doFilter: false,
            filteredData: List(filteredData),
        });
    }

    private async generateExport() {
        if (this.state.generatingExport)
            return;

        await this.setStateAsync({ ...this.state, generatingExport: true });

        // get status ID
        const urlQuery = QueryString.parse(this.props.location.search) as UrlQuery;
        const statusIds = (urlQuery.status || "").split(",").filter((x) => x !== "").map((x) => parseInt(x, 10));
        const statusId = statusIds.length ? statusIds[0] : undefined;

        const eventId = this.props.eventId;
        let authorIds = this.state.selectedIds.map((x) => parseInt(x.split("_")[1], 10));

        // if filtering by search and no selected author IDs, get
        // all author IDs from search results
        if (this.state.tableSearchResults && !authorIds.length)
            authorIds = this.state.tableSearchResults.map((x: M.Abstract.AbstractAuthorTableItem) => x.id);

        const body = {
            authorIds,
            eventId,
            statusId,
        };

        const result = await this.props.api.post(Api.endpoints.admin.abstract.exportDisclosures.withUrlParams({ eventId }), {
            body: JSON.stringify(body),
        });

        if (!this._isMounted)
            return;

        await this.setStateAsync({ ...this.state, generatingExport: false });

        if (result.isSuccess && result.data) {
            const endpoint =  Api.endpoints.file.tempFile.withUrlParams({ token: result.data });
            const path = Settings.ApiUrl + ApiEndpoint.getFormattedUrl(endpoint) + "?access_token=Bearer%20" + encodeURIComponent(this.props.authToken);
            DocumentHelper.postForm(path, {});
        }
        else {
            this.props.notifyError(M.Result.Result.errorsOrMessage(result).join("<br />"));
        }
    }

    private getFilterTypeName(type: FilterType) {
        switch (type) {
            case "status": return "Status";
            default: return "Unknown";
        }
    }

    private async initializeComponent() {
        const eventId = this.props.eventId;

        await this.loadAbstractsFromState();
        await this.loadFDsFromState();
        await this.updateAllFilterChecks(this.props.location.search);
        this.props.loadAbstracts(eventId);
        this.props.loadFDs(eventId);
    }

    private lastPathIsFromListView() {
        return this.props.lastPath === `${Paths.AdminAbstracts.BasePath}/${this.props.eventId}`
            || (this.props.lastPath || "").indexOf(`${Paths.AdminAbstracts.BasePath}/${this.props.eventId}?`) === 0;
    }

    private async loadAbstractsFromState(toggleLoading: boolean = false) {
        const eventId = this.props.eventId;
        const abstracts = this.props.abstracts
            .filter((x: M.Abstract.AbstractListItem) => x.eventId === eventId && !M.Abstract.AbstractStatuses.isUnsubmitted(x))
            .toList();

        if (!abstracts.size && toggleLoading)
            this.props.toggleAbstractsLoading(true);

        await this.mergeData(abstracts, this.props.FDs);
        await this.filterData();
        await this.updateFilterCounts();

        // update table state from location state if available
        this.updateTableState(this.state.authors);
    }

    private async loadFDsFromState(toggleLoading: boolean = false) {
        const eventId = this.props.eventId;
        const abstracts = this.props.abstracts.filter((x: M.Abstract.AbstractListItem) => x.eventId === eventId).toList();

        if (!this.props.FDs.size && toggleLoading)
            this.props.toggleFDsLoading(true);

        if (this.props.FDs.size && abstracts.size)
            await this.loadAbstractsFromState(toggleLoading);
    }

    private async mergeData(abstracts: List<M.Abstract.AbstractListItem>, FDs: List<M.FinancialDisclosure.FD>) {
        let authors: List<M.Abstract.AbstractAuthorTableItem> = List();

        abstracts.forEach((x: M.Abstract.AbstractListItem) => {
            const primaryAuthors = x.authors.filter((a) => a.isPrimary);
            for (const author of x.authors) {
                const FD = FDs.find((fd: M.FinancialDisclosure.FD) => author.legacyMemberId !== undefined && fd.legacyMemberId === author.legacyMemberId);

                const a: M.Abstract.AbstractAuthorTableItem = {
                    ...author,
                    abstractGuid: x.guid,
                    abstractId: x.id,
                    abstractIdSearch: "#" + x.id + "#",
                    abstractTitle: x.title,
                    authorName: primaryAuthors.length ? M.Generic.PersonHelper.getFullName(primaryAuthors[0]) : "",
                    eventId: x.eventId,
                    FD,
                    FDsDisplay: FD ? M.FinancialDisclosure.getFDsFormatted(FD) : "",
                    fullName: `${author.lastName}, ${author.firstName}`,
                    isCoi: x.isCoi,
                    lineId: `${x.id}_${author.id}`,
                    statusId: FD === undefined
                        ? M.FinancialDisclosure.DisclosureStatuses.HasNoDisclosure.id
                        : M.FinancialDisclosure.DisclosureStatuses.HasDisclosure.id,
                };
                authors = authors.push(a);
            }
        });

        await this.setStateAsync({ ...this.state, authors });
    }

    /**
     * Event handler when filter item is [un]checked
     * @param id ID of the filter
     * @param checked Whether filter was [un]checked
     * @param type The filter type
     */
    private async onFilterCheck(id: string | number, checked: boolean, type: FilterType) {
        const urlQuery = QueryString.parse(this.props.location.search) as UrlQuery;
        let filterIds = Utility.commaStringToIntArray(urlQuery[type]);

        if (type === "status") {
            if (!checked)
                filterIds = [];
            else {
                filterIds = [parseInt(id.toString(), 10)];
                urlQuery[type] = filterIds.join(",");
            }
        }
        else {
            Utility.addToOrRemoveFromArray(filterIds, id, checked);
            urlQuery[type as string] = filterIds.join(",");
        }

        this.props.replaceUrlQuery(urlQuery);
        this.saveTableState();
    }

    private async sendFdRequest(selectedIds: string[], isBatch: boolean) {
        if (!selectedIds.length || this.state.sendingFdRequestBatch)
            return;

        if (isBatch)
            await this.setStateAsync({ ...this.state, sendingFdRequestBatch: true });
        else {
            const sendingFdRequest = { ...this.state.sendingFdRequest };
            sendingFdRequest[selectedIds[0]] = true;
            await this.setStateAsync({ ...this.state, sendingFdRequest });
        }

        const abstractIds = _.uniq(selectedIds.map((x) => parseInt(x.split("_")[0], 10)));
        const authorIds = selectedIds.map((x) => parseInt(x.split("_")[1], 10));
        const result = await this.props.api.post(Api.endpoints.admin.abstract.batchSendFdRequest, {
            body: JSON.stringify(authorIds),
        });

        if (!this._isMounted)
            return;

        if (result.errors.length || result.message)
            this.props.notifyError(M.Result.Result.errorsOrMessage(result).join("<br />"));

        if (result.isSuccess && result.data) {
            // sync authors updated
            this.props.notifySuccess(`Disclosure request has been sent to ${result.data.length} author${result.data.length === 1 ? "" : "s"}`);

            // update state of current authors
            const authors = this.state.authors.toList();
            const authorAbstracts = authors.filter((x: M.Abstract.AbstractAuthorTableItem) => authorIds.indexOf(x.id) > -1);
            authorAbstracts.forEach((x: M.Abstract.AbstractAuthorTableItem) => {
                const resultAuthor = _.find(result.data, (d) => d.id === x.id);
                x.lastFdRequestSent = resultAuthor ? resultAuthor.lastFdRequestSent : null;
            });

            await this.setStateAsync({ ...this.state, authors });

            // update state of abstracts (in-app)
            const abstracts = this.props.abstracts.filter((x: M.Abstract.AbstractListItem) => abstractIds.indexOf(x.id) > -1).toList();
            abstracts.forEach((x) => {
                if (x) {
                    const _authors = x.authors.filter((a) => authorIds.indexOf(a.id) > -1);
                    for (const a of _authors) {
                        const resultAuthor = _.find(result.data, (d) => d.id === x.id);
                        a.lastFdRequestSent = resultAuthor ? resultAuthor.lastFdRequestSent : null;
                    }
                }
            });
            console.log(abstracts.toArray());
            this.props.updateAbstracts(abstracts);
        }

        if (isBatch)
            await this.setStateAsync({ ...this.state, sendingFdRequestBatch: false });
        else {
            const sendingFdRequest = { ...this.state.sendingFdRequest };
            sendingFdRequest[selectedIds[0]] = false;
            await this.setStateAsync({ ...this.state, sendingFdRequest });
        }
    }

    private async updateAllFilterChecks(query?: string) {
        // get filtering maps from state
        const statuses = [ ...this.state.statuses ];
        const appliedFilters: M.Search.AppliedFilter[] = [];

        this.updateFilterChecks(statuses, "status", appliedFilters, query);

        await this.setStateAsync({
            ...this.state,
            appliedFilters,
            selectedIds: [],
            statuses,
        });

        await this.setStateAsync({
            ...this.state,
            doFilter: true,
        });

        await this.updateFilterCounts();
    }

    private async updateCoiFlag(abstractId: number, isCoi: boolean, doApiCall: boolean = true) {
        const abstract = this.props.abstracts.find((x: M.Abstract.AbstractListItem) => x.id === abstractId);
        if (!abstract) {
            this.props.notifyError(`Abstract not found for id ${abstractId}`);
            return;
        }

        const authors = this.state.authors.toList();
        const authorAbstracts = authors.filter((x: M.Abstract.AbstractAuthorTableItem) => x.abstractId === abstractId);
        authorAbstracts.forEach((x: M.Abstract.AbstractAuthorTableItem) => {
            x.isCoi = isCoi;
        });

        await this.setStateAsync({ ...this.state, authors });

        abstract.isCoi = isCoi;
        this.props.updateAbstract(abstract);

        const qp = { queryParams: { isCoi }};
        const result = await this.props.api.post(Api.endpoints.admin.abstract.coi.withUrlParams({ guid: abstract.guid }), qp);

        if (!this._isMounted)
            return;

        if (!result.isSuccess) {
            this.props.notifyError(M.Result.Result.errorsOrMessage(result).join("<br />"));
        }
    }

    /**
     * Resets checked filters based on current url query
     */
    private updateFilterChecks(filters: M.Search.SearchFilter[], type: FilterType, appliedFilters: M.Search.AppliedFilter[], query?: string) {
        query = query === undefined ? this.props.location.search : query;
        const urlQuery = QueryString.parse(query);
        const itemIds = Utility.commaStringToIntArray(urlQuery[type]);

        for (const item of filters) {
            item.checked = itemIds.indexOf(item.id) > -1;
            if (item.checked) {
                appliedFilters.push({ id: item.id, name: item.name, type, typeName: this.getFilterTypeName(type) });

                if (type === "status")
                    break;
            }
        }
    }

    private async updateFilterCounts(forFilters?: FilterType[]) {
        // get filtering maps from state
        const statuses = [ ...this.state.statuses ];

        const list = this.state.tableSearchResults ? List(this.state.tableSearchResults) : this.state.authors;

        if (!forFilters || forFilters.indexOf("status") > -1) {
            for (const f of statuses) {
                // special case for status (COI is not really a status)
                if (f.id === M.FinancialDisclosure.DisclosureStatuses.IsCOI.id)
                    f.count = list.filter((a: M.Abstract.AbstractAuthorTableItem) => a.isCoi).size;
                else
                    f.count = list.filter((a: M.Abstract.AbstractAuthorTableItem) => f.id === a.statusId).size;
            }
        }

        await this.setStateAsync({
            ...this.state,
            statuses,
        });
    }
}

export const Disclosures: React.ComponentClass<OwnProps> = connect(mapStateToProps, mapDispatchToProps)(withRouter(DisclosuresComponent) as any);



// WEBPACK FOOTER //
// ./src/containers/adminAbstracts/disclosures/Disclosures.tsx